/*
 *  Header Navigation
 *    Scripts for the header navigation
 *
 *  Version:
 *    1.0.0 - 2020/10/19
 */

import { delegate } from '../../tools/utilities/event-tools';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import {
  CLASS_SUBMENU_OPEN,
  CLASS_SUBSUBMENU_OPEN,
  CLASS_HIDDEN,
  CLASS_ALT,
  CLASS_ACTIVE,
  CLASS_SELECTED,
} from '../../tools/constants/classes';
import {
  responsiveMediaAbove,
  responsiveMediaBelow,
} from '../../settings/responsive';
import {
  trapFocus,
  removeTrapFocus
} from '../../tools/utilities/trap-focus';

// Main Selectors
const SELECTOR_HEADER = '[data-header]';
const SELECTOR_HEADER_NAVIGATION = '[data-header-navigation]';

// Containers
const SELECTOR_SUBMENU_CONTAINER = '[data-submenu-container]';
const SELECTOR_SUBMENU_INNER = '[data-submenu-inner]';
const SELECTOR_SUBSUBMENU_CONTAINER = '[data-subsubmenu-container]';
const SELECTOR_SUBSUBMENU_INNER = '[data-subsubmenu-inner]';
const SELECTOR_NOSUBSUBMENU_CONTAINER = '[data-nosubsubmenu-container]';
const SELECTOR_NESTED_CONTAINER = '[data-nested-container]';

// Mobile triggers
const SELECTOR_MOBILE_SUBMENU_OPEN = '[data-mobile-submenu-open]';
const SELECTOR_MOBILE_SUBSUBMENU_OPEN = '[data-mobile-subsubmenu-open]';

const SELECTOR_MOBILE_SUBMENU_BACK = '[data-mobile-submenu-back]';
const SELECTOR_MOBILE_SUBSUBMENU_BACK = '[data-mobile-subsubmenu-back]';

// Scroll enable selectors
const SELECTOR_ENABLE_SCROLL_PARENT = '[data-enable-scroll-parent]';
const SELECTOR_ENABLE_SCROLL_SUBMENU = '[data-enable-scroll-submenu]';
const SELECTOR_ENABLE_SCROLL_SUBSUBMENU = '[data-enable-scroll-subsubmenu]';

// Mobile search trigger
const SELECTOR_MOBILE_SEARCH = '[data-mobile-search-trigger]';
const SELECTOR_SEARCH_INPUT = '[data-search-input]';

// First level mobile menu closer
const SELECTOR_MOBILE_MENU_CLOSE_FIRST = '[data-close-mobile-menu-first]';

// Global Variable
// keep track of last nested link hovered
let lastNestedLink = document.querySelector<HTMLElement>(
  SELECTOR_SUBSUBMENU_CONTAINER
);

// Functions
const onMouseOver = (element: HTMLElement) => {
  // Don't run on mobile
  if (responsiveMediaBelow('MEDIUM')) return;
  const header = element.closest(SELECTOR_HEADER);
  const subSubmenu = element.closest(SELECTOR_NESTED_CONTAINER);

  if (!header) return;

  header.classList.add(CLASS_ALT);

  if (subSubmenu) {
    subSubmenu.classList.add(CLASS_SUBMENU_OPEN);
  } else {
    element.classList.add(CLASS_SUBMENU_OPEN);
  }
};

const onMouseLeave = (element: HTMLElement) => {
  // Don't run on mobile
  if (responsiveMediaBelow('MEDIUM')) return;
  const header = element.closest(SELECTOR_HEADER);
  const subSubmenu = element.closest(SELECTOR_NESTED_CONTAINER);
  const nestedLink = subSubmenu?.querySelector<HTMLElement>(
    SELECTOR_SUBSUBMENU_CONTAINER
  );

  if (!header) return;

  header.classList.remove(CLASS_ALT);

  if (subSubmenu) {
    subSubmenu.classList.remove(CLASS_SUBMENU_OPEN);
    // keep the first nested link active if it's not the lastNestedLink
    if (nestedLink === lastNestedLink) {
      nestedLink?.classList.add(CLASS_ACTIVE);
    }
  } else {
    element.classList.remove(CLASS_SUBMENU_OPEN);
  }
};

// Last hovered Submenu item stays hovered until hover on something else
const onMouseMove = () => {
  if (responsiveMediaBelow('MEDIUM')) return;
  if (!lastNestedLink?.classList.contains(CLASS_ACTIVE)) {
    lastNestedLink?.classList.add(CLASS_ACTIVE);
  }
};

const onSubmenuOpen = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  evt.preventDefault();

  const target = evt.target as HTMLElement;
  const submenu = target.closest(SELECTOR_SUBMENU_CONTAINER);

  // Check submenu exists
  if (submenu === null) return;

  // Open
  submenu.classList.add(CLASS_SUBMENU_OPEN);

  // Disable body scroll
  clearAllBodyScrollLocks();
  const targetElement = submenu.querySelector<HTMLElement>(SELECTOR_ENABLE_SCROLL_SUBMENU);
  if (targetElement) disableBodyScroll(targetElement);

  // Trap focus inside menu
  const targetFocusContainer = submenu.querySelector<HTMLElement>(SELECTOR_SUBMENU_INNER);
  const submenuBackButton = targetFocusContainer.querySelector<HTMLElement>(SELECTOR_MOBILE_SUBMENU_BACK);
  targetFocusContainer.addEventListener('transitionend', () => {
    trapFocus(targetFocusContainer, submenuBackButton);
  }, { once: true });
};

const onSubmenuClose = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  evt.preventDefault();

  const target = evt.target as HTMLElement;
  const submenu = target.closest(SELECTOR_SUBMENU_CONTAINER);
  const header = target.closest(SELECTOR_HEADER);

  // Check submenu exists
  if (submenu === null) return;

  // Close
  submenu.classList.remove(CLASS_SUBMENU_OPEN);

  // Disable body scroll
  clearAllBodyScrollLocks();
  const targetElement = header.querySelector<HTMLElement>(
    SELECTOR_ENABLE_SCROLL_PARENT
  );
  if (targetElement) disableBodyScroll(targetElement);

  // remove trap focus and trap inside parent
  const headerNavigationElement = document.querySelector<HTMLElement>(
    `${SELECTOR_HEADER} ${SELECTOR_HEADER_NAVIGATION}`
  );
  const closeMobileMenuFirstBtn = document.querySelector<HTMLElement>(
    `${SELECTOR_HEADER} ${SELECTOR_MOBILE_MENU_CLOSE_FIRST}`
  );
  removeTrapFocus();
  trapFocus(headerNavigationElement, closeMobileMenuFirstBtn);
};

const onSubSubmenuOpen = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  const target = evt.target as HTMLElement;
  const subSubmenu = target.closest(SELECTOR_SUBSUBMENU_CONTAINER) as HTMLElement;

  evt.preventDefault();

  // Check subSubmenu exists
  if (subSubmenu === null) return;

  // Open subSubmenu
  subSubmenu.classList.add(CLASS_SUBSUBMENU_OPEN);

  // Disable body scroll
  clearAllBodyScrollLocks();
  const targetElement = subSubmenu.querySelector<HTMLElement>(SELECTOR_ENABLE_SCROLL_SUBSUBMENU);
  if (targetElement) disableBodyScroll(targetElement);

  // Trap focus inside menu
  const targetFocusContainer = subSubmenu.querySelector<HTMLElement>(SELECTOR_SUBSUBMENU_INNER);
  const subSubmenuBackButton = targetFocusContainer.querySelector<HTMLElement>(SELECTOR_MOBILE_SUBSUBMENU_BACK);
  targetFocusContainer.addEventListener('transitionend', () => {
    trapFocus(targetFocusContainer, subSubmenuBackButton);
  }, { once: true });
};

const onSubSubmenuClose = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  const target = evt.target as HTMLElement;
  const subSubmenu = target.closest(SELECTOR_SUBSUBMENU_CONTAINER) as HTMLElement;
  const subMenu = target.closest(SELECTOR_SUBMENU_CONTAINER) as HTMLElement;

  evt.preventDefault();

  // Check subSubmenu exists
  if (subSubmenu === null) return;

  // Close subSubmenu
  subSubmenu.classList.remove(CLASS_SUBSUBMENU_OPEN);

  // Disable body scroll
  clearAllBodyScrollLocks();
  const targetElement = subMenu.querySelector<HTMLElement>(
    SELECTOR_ENABLE_SCROLL_SUBMENU
  );
  if (targetElement) disableBodyScroll(targetElement);

  // remove trap focus and trap inside parent
  const targetFocusContainer = subMenu.querySelector<HTMLElement>(
    SELECTOR_SUBMENU_INNER
  );
  const submenuBackButton = targetFocusContainer.querySelector<HTMLElement>(SELECTOR_MOBILE_SUBMENU_BACK);
  removeTrapFocus();
  trapFocus(targetFocusContainer, submenuBackButton);
};

// When input field has a value, clicking on the search trigger will submit the form
const onSubmit = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  evt.preventDefault();

  const searchInput = document.querySelector<HTMLInputElement>(
    SELECTOR_SEARCH_INPUT
  );

  // Check search input exists
  if (searchInput === null) return;

  // Check search input has a value
  if (searchInput.value) {
    // Submit form
    searchInput.form?.submit();
  } else {
    searchInput.focus();
    searchInput.select();
  }
};

/*
  Events
*/
// Menu overlay hover events
// Main nav hover - Shop, Learn, Support
document
  .querySelectorAll<HTMLElement>(SELECTOR_SUBMENU_CONTAINER)
  .forEach((element) => {
    element.addEventListener('mouseover', () => onMouseOver(element));
    element.addEventListener('mouseleave', () => onMouseLeave(element));
    element.addEventListener('mousemove', () => onMouseMove());
  });

// Submenu hover with nested links - Tools, Skincare
document
  .querySelectorAll<HTMLElement>(SELECTOR_SUBSUBMENU_CONTAINER)
  .forEach((element) => {
    element.addEventListener('mouseover', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      element.classList.add(CLASS_ACTIVE);
      if (lastNestedLink !== element) {
        lastNestedLink?.classList.remove(CLASS_ACTIVE);
        lastNestedLink = element;
      }
    });
    element.addEventListener('mouseleave', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      element.classList.remove(CLASS_ACTIVE);
    });
    element.addEventListener('focusin', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      document
        .querySelectorAll<HTMLElement>(SELECTOR_SUBSUBMENU_CONTAINER)
        .forEach((el) => el.classList.remove(CLASS_ACTIVE));      
      element.classList.add(CLASS_ACTIVE);
    });    
  });
// Submenu hover without nested links - Shop All will open a hover menu of the previous nested link
document
  .querySelectorAll<HTMLElement>(SELECTOR_NOSUBSUBMENU_CONTAINER)
  .forEach((noSubElement) => {
    noSubElement.addEventListener('mouseover', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      lastNestedLink?.classList.add(CLASS_SELECTED);
      if (lastNestedLink?.classList.contains(CLASS_ACTIVE)) {
        lastNestedLink?.classList.remove(CLASS_ACTIVE);
      }
    });
    noSubElement.addEventListener('mouseleave', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      lastNestedLink?.classList.remove(CLASS_SELECTED);
    });
  });

document
  .querySelectorAll<HTMLElement>(SELECTOR_MOBILE_SEARCH)
  .forEach((element) => {
    element.addEventListener('click', () => {
      const searchInput = document.querySelector<HTMLInputElement>(
        SELECTOR_SEARCH_INPUT
      );
      if (!searchInput) return;
      searchInput.focus();
      searchInput.select();
    });
  });

// On submenu open button click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SUBMENU_OPEN,
  event: 'click',
  callback: onSubmenuOpen,
});

// On submenu close (back) button click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SUBMENU_BACK,
  event: 'click',
  callback: onSubmenuClose,
});

// On subSubmenu open button click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SUBSUBMENU_OPEN,
  event: 'click',
  callback: onSubSubmenuOpen,
});

// On subSubmenu close (back) button click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SUBSUBMENU_BACK,
  event: 'click',
  callback: onSubSubmenuClose,
});

// On search trigger click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SEARCH,
  event: 'click',
  callback: onSubmit,
});
